import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Megaphone, Mail, Trash2, Eye } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * Coach broadcast composer — sits on /coach below the students roster.
 * Coach types a message → fans out to every active student via in-app notification
 * (and Resend email if they tick the box). Shows recent broadcasts with read-counts.
 */
export default function CoachBroadcastSection() {
  const [form, setForm] = useState({ title: "", body: "", send_email: false });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadHistory = useCallback(() => {
    api.get("/coach/me/messages").then((r) => setHistory(r.data || [])).catch(() => setHistory([]));
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.body.trim()) { toast.error("Message body is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/coach/me/messages", form);
      if (data.no_students) {
        toast.warning("You don't have any students yet — share your enrollment QR first.");
      } else {
        const emailSuffix = data.email_sent ? ` · ${data.email_sent} email${data.email_sent === 1 ? "" : "s"}` : "";
        toast.success(`Sent to ${data.delivered} student${data.delivered === 1 ? "" : "s"}${emailSuffix}`);
      }
      setForm({ title: "", body: "", send_email: false });
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this broadcast? Students keep their notification history.")) return;
    try {
      await api.delete(`/coach/me/messages/${id}`);
      toast.success("Deleted");
      loadHistory();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <section className="mt-10" data-testid="coach-broadcast-section">
      <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
        <Megaphone size={20} className="text-[#00E676]" /> Broadcast to my students
      </h2>
      <p className="text-zinc-500 text-sm mt-1">Drop a message once — every enrolled student sees it on their dashboard.</p>

      <form onSubmit={submit} className="mt-4 pp-card-elevated p-5 space-y-3 max-w-2xl">
        <input className="pp-input" placeholder="Title (optional)" data-testid="broadcast-title"
          maxLength={200} value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
        <textarea className="pp-input" rows={3} required placeholder="Your message…"
          data-testid="broadcast-body" maxLength={5000} value={form.body}
          onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} />
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={form.send_email} data-testid="broadcast-email"
              onChange={(e) => setForm((f) => ({ ...f, send_email: e.target.checked }))} />
            <Mail size={12} /> Also send via email
          </label>
          <button disabled={busy} data-testid="broadcast-send"
            className="pp-btn-primary px-5 py-2.5 rounded-xl text-sm flex items-center gap-2">
            <Megaphone size={14} /> {busy ? "Sending…" : "Send broadcast"}
          </button>
        </div>
      </form>

      {history.length > 0 && (
        <div className="mt-6">
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Recent broadcasts</div>
          <div className="space-y-2 max-w-2xl" data-testid="broadcast-history">
            {history.slice(0, 8).map((m) => (
              <div key={m.id} className="pp-card p-4" data-testid={`broadcast-row-${m.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {m.title && <div className="font-bold text-sm">{m.title}</div>}
                    <div className="text-sm text-zinc-300 mt-0.5 whitespace-pre-wrap break-words">{m.body}</div>
                    <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-3">
                      <span>{new Date(m.created_at).toLocaleString()}</span>
                      <span className="flex items-center gap-1"><Eye size={11} /> {m.read_count}/{m.recipient_count} read</span>
                      {m.send_email && <span className="pp-chip text-[10px]"><Mail size={9} className="inline" /> email</span>}
                    </div>
                  </div>
                  <button onClick={() => remove(m.id)} title="Delete" data-testid={`broadcast-delete-${m.id}`}
                    className="p-2 text-zinc-500 hover:text-[#FF3B30]"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
